import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { UsertokenService } from '@shared-angular/services/usertoken.service';
import { SpfBucketFileFrontend } from '@shared/index';
import { aiImageAnalyzeSchema, aiImageCreateSchema, aiImageFileIdSchema } from '@shared/ragai.schemas';
import { ImageGenerateParams } from 'openai/resources';
import QueryString from 'qs';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { EventManagerService } from '../../event-manager/event-manager.service';

export type AiImageFile = SpfBucketFileFrontend<{
  userId: string,
  botId: string,
  created: Date,
  lastAccess: Date,
  prompt: string
}>

/** Erzeugt und analysiert Bilder per OpenAI (`RAGAI.AIIMAGE_OPENAI`). */
/**
 * Erzeugt und analysiert Bilder per OpenAI (`RAGAI.AIIMAGE_OPENAI`).
 * @remarks
 * {@link create} generiert per Standard ein neues Bild; über `opts.image`/`opts.images` wird stattdessen eine
 * Bildbearbeitung (Referenzbild(er) als Basis) angestoßen. {@link analyzeImage} ist die einzige der drei
 * Bild-Provider-Klassen (`AIIMAGE_OPENAI`/`_FALAI`/`_STABLEDIFFUSION`), die auch Bild-**Analyse** (Vision, nicht
 * nur Generierung) anbietet - die anderen beiden können nur erzeugen/bearbeiten.
 */
@Injectable({
  providedIn: 'root'
})
export class OpenaiImageService {
  constructor(
    private eventManager: EventManagerService,
    private http: HttpClient,
    private botIdService: BotidService,
    private usertokenService: UsertokenService
  ) { }

  /**
   * Generiert ein Bild per OpenAI-Modell.
   * @param prompt Der Text-Prompt oder ein Objekt gemäß `aiImageCreateSchema`.
   * @param opts Zusätzliche OpenAI-Bildoptionen.
   * @returns Die erzeugte Bilddatei.
   */
  public async create(prompt: string | z.input<typeof aiImageCreateSchema>, opts: Omit<ImageGenerateParams, "prompt"> & { image?: string, images?: string[], aspectRatio?: string } = {}): Promise<AiImageFile> {
    const parsed = aiImageCreateSchema.safeParse(prompt);
    if (parsed.success && typeof prompt === 'object') {
      const { prompt: parsedPrompt, ...rest } = parsed.data;
      prompt = parsedPrompt;
      opts = { ...rest, ...opts };
    }
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AIIMAGE_OPENAI.CREATE.start", { prompt, opts });

    const query = QueryString.stringify({
      prompt,
      ...opts,
      ...(opts.images && { images: JSON.stringify(opts.images) })
    });
    const result = await firstValueFrom(this.http.get<AiImageFile>("/api/ai/image/create/openai/" + this.botIdService.getBotId() + "?" + query));
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AIIMAGE_OPENAI.CREATE.done", { prompt, opts, result });


    return result;
  }

  /**
   * Erstellt eine ladbare URL für ein zuvor erzeugtes Bild.
   * @param fileId ID der Bilddatei oder ein Objekt gemäß `aiImageFileIdSchema`.
   * @returns Die URL zum Laden des Bildes.
   */
  public getImageUrl(fileId: string | z.input<typeof aiImageFileIdSchema>): string {
    const parsed = aiImageFileIdSchema.safeParse(fileId);
    if (parsed.success && typeof fileId === 'object') {
      fileId = parsed.data.fileId;
    }
    return "/api/ai/image/load/" + this.botIdService.getBotId() + "/" + (fileId as string) + "?Ut=" + this.usertokenService.getUserToken();
  }

  /**
   * Lässt ein Bild von OpenAI analysieren und beschreiben.
   * @param image Bild-URL oder ein Objekt gemäß `aiImageAnalyzeSchema`.
   * @param prompt Zusätzliche Anweisung für die Analyse.
   * @returns Die von der KI erzeugte Bildbeschreibung.
   */
  public async analyzeImage(image: string | z.input<typeof aiImageAnalyzeSchema>, prompt: string = ''): Promise<string> {
    const parsed = aiImageAnalyzeSchema.safeParse(image);
    if (parsed.success && typeof image === 'object') {
      prompt = parsed.data.prompt;
      image = parsed.data.image;
    }
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AIIMAGE_OPENAI.ANALYZEIMAGE.start", { image, prompt });
    const result = await firstValueFrom(this.http.post<string>('/api/user/image/analyze/' + this.botIdService.getBotId(), { text: prompt, image }));
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AIIMAGE_OPENAI.ANALYZEIMAGE.done", { image, prompt, result });
    return result;
  }
}
